import React, { useEffect, useState } from 'react'
import globalNotification from 'live/services/global-notification'
import { Button } from 'react-bootstrap'
import { BodyRow, HeadRow, StyledCardContainer, Table } from '../styles/styles'
import List, { OrderingHeader, toggleOrder } from 'live/components/ui/List'
import dateService from 'live/services/date'

interface Props {
    selected: {
        asset: {
            id: string
            name: string
        }
    }
}

interface PropsLog {
    log: Log
}

interface Log {
    logName: string
    indexType: string
    startDateTimeIndex?: number
    endDateTimeIndex?: number
    startIndex?: number
    endIndex?: number
    logCurveInfo: LogCurveInfo[]
}

interface LogCurveInfo {
    mnemonic: string
    unit: string
    minDateTimeIndex?: number
    maxDateTimeIndex?: number
    minIndex?: number
    maxIndex?: number
    normalizeToMnemonic?: string
    isNormalizing?: boolean
    prioritySet?: number
    priorityExpected?: number
}

interface Order {
    prop: string
    dir: 'asc' | 'desc'
}

const DEFAULT_ORDER: Order = { prop: 'listId', dir: 'asc' }

const Container: React.FC<PropsLog> = ({ log }: PropsLog) => {
    const [collapsed, setCollapsed] = useState(true)
    const [order, setOrder] = React.useState<Order>(DEFAULT_ORDER)

    let channelCount = 0
    let activeCount = 0

    log.logCurveInfo.forEach(curve => {
        channelCount++
        if (curve.isNormalizing) {
            activeCount++
        }
    })

    function sortTable(field: string): void {
        setOrder(s => {
            const toggledOrder = toggleOrder(s, field, 'asc')

            if (field === 'mnemonic') {
                toggledOrder.fn = (a: LogCurveInfo, b: LogCurveInfo) =>
                    toggledOrder.dir === 'asc'
                        ? a.mnemonic.localeCompare(b.mnemonic)
                        : b.mnemonic.localeCompare(a.mnemonic)
            } else if (field === 'unit') {
                toggledOrder.fn = (a: LogCurveInfo, b: LogCurveInfo) =>
                    toggledOrder.dir === 'asc' ? a.unit.localeCompare(b.unit) : b.unit.localeCompare(a.unit)
            } else if (field === 'minIndex') {
                toggledOrder.fn = (a: LogCurveInfo, b: LogCurveInfo) =>
                    toggledOrder.dir === 'asc' ? a.minIndex ?? 0 - b.minIndex ?? -1 : b.minIndex ?? 0 - a.minIndex ?? -1
            } else if (field === 'maxIndex') {
                toggledOrder.fn = (a: LogCurveInfo, b: LogCurveInfo) =>
                    toggledOrder.dir === 'asc' ? a.maxIndex ?? 0 - b.maxIndex ?? -1 : b.maxIndex ?? 0 - a.maxIndex ?? -1
            } else if (field === 'minDateTimeIndex') {
                toggledOrder.fn = (a: LogCurveInfo, b: LogCurveInfo) =>
                    toggledOrder.dir === 'asc'
                        ? a.minDateTimeIndex ?? 0 - b.minDateTimeIndex ?? -1
                        : b.minDateTimeIndex ?? 0 - a.minDateTimeIndex ?? -1
            } else if (field === 'maxDateTimeIndex') {
                toggledOrder.fn = (a: LogCurveInfo, b: LogCurveInfo) =>
                    toggledOrder.dir === 'asc'
                        ? a.maxDateTimeIndex ?? 0 - b.maxDateTimeIndex ?? -1
                        : b.maxDateTimeIndex ?? 0 - a.maxDateTimeIndex ?? -1
            } else if (field === 'normalizeToMnemonic') {
                toggledOrder.fn = (a: LogCurveInfo, b: LogCurveInfo) =>
                    toggledOrder.dir === 'asc'
                        ? a.normalizeToMnemonic.localeCompare(b.normalizeToMnemonic)
                        : b.normalizeToMnemonic.localeCompare(a.normalizeToMnemonic)
            } else {
                delete toggledOrder.fn
            }

            return toggledOrder
        })
    }

    return (
        <StyledCardContainer
            title={`${log.logName}`}
            subtitle={` - ${log.indexType}`}
            collapsed={collapsed}
            renderControls={() => {
                return (
                    <Button onClick={() => setCollapsed(!collapsed)}>
                        <strong>{activeCount}</strong> of <strong>{channelCount}</strong> active
                        <i
                            className={collapsed ? 'far fa-angle-up' : 'far fa-angle-down'}
                            style={{ marginLeft: '8px' }}
                        />
                    </Button>
                )
            }}
        >
            <List
                sort={true}
                items={log.logCurveInfo}
                order={order}
                render={(items: LogCurveInfo[]) => {
                    return (
                        <Table>
                            <HeadRow>
                                <OrderingHeader></OrderingHeader>
                                <OrderingHeader
                                    onClick={() => sortTable('mnemonic')}
                                    showArrow={order.prop === 'mnemonic'}
                                    direction={order.dir}
                                >
                                    Mnemonic
                                </OrderingHeader>
                                <OrderingHeader
                                    onClick={() => sortTable('unit')}
                                    showArrow={order.prop === 'unit'}
                                    direction={order.dir}
                                >
                                    Unit
                                </OrderingHeader>
                                {log.indexType === 'measured depth' && (
                                    <>
                                        <OrderingHeader
                                            onClick={() => sortTable('minIndex')}
                                            showArrow={order.prop === 'minIndex'}
                                            direction={order.dir}
                                        >
                                            Start
                                        </OrderingHeader>
                                        <OrderingHeader
                                            onClick={() => sortTable('maxIndex')}
                                            showArrow={order.prop === 'maxIndex'}
                                            direction={order.dir}
                                        >
                                            End
                                        </OrderingHeader>
                                    </>
                                )}
                                {log.indexType === 'date time' && (
                                    <>
                                        <OrderingHeader
                                            onClick={() => sortTable('minDateTimeIndex')}
                                            showArrow={order.prop === 'minDateTimeIndex'}
                                            direction={order.dir}
                                        >
                                            Start
                                        </OrderingHeader>
                                        <OrderingHeader
                                            onClick={() => sortTable('maxDateTimeIndex')}
                                            showArrow={order.prop === 'maxDateTimeIndex'}
                                            direction={order.dir}
                                        >
                                            End
                                        </OrderingHeader>
                                    </>
                                )}
                                <OrderingHeader>
                                    Normalize To
                                </OrderingHeader>
                            </HeadRow>
                            {items.map(curve => (
                                <BodyRow key={`${curve.mnemonic}`}>
                                    <div>
                                        {curve.isNormalizing ? (
                                            <i className="far fa-check-circle fa-lg" title="Channel is normaling"></i>
                                        ) : curve.priorityExpected !== curve.prioritySet ? (
                                            <i
                                                className="fas fa-exclamation-circle fa-lg"
                                                title={`Auto-switch set to ${curve.prioritySet}, expected ${curve.priorityExpected}`}
                                            ></i>
                                        ) : (
                                            <i className="fas fa-times fa-lg" title={`Not normalizing`}></i>
                                        )}
                                    </div>
                                    <div>
                                        <strong>{curve.mnemonic}</strong>
                                    </div>
                                    <div>{curve.unit}</div>
                                    <div>
                                        {log.indexType === 'measured depth'
                                            ? curve.minIndex
                                            : dateService.formatDateTime(curve.minDateTimeIndex)}
                                    </div>
                                    <div>
                                        {log.indexType === 'measured depth'
                                            ? curve.maxIndex
                                            : dateService.formatDateTime(curve.maxDateTimeIndex)}
                                    </div>
                                    <div>{curve.normalizeToMnemonic}</div>
                                </BodyRow>
                            ))}
                        </Table>
                    )
                }}
            />
        </StyledCardContainer>
    )
}

export const LogViewer: React.FC<Props> = ({ selected }: Props) => {
    const [logs, setLogs] = useState<Log[]>([])

    useEffect(() => {
        const url = `services/plugin-asset-setup/asset/getLogs?wellboreId=${selected.asset.id}`

        fetch(url)
            .then(result => {
                if (result.ok) {
                    return result.json()
                } else {
                    throw new Error()
                }
            })

            .then(json => setLogs(json))
            .catch(e => globalNotification.error(e.message ?? 'An error occurred on fetching log headers'))
    }, [selected.asset.id])

    return (
        <div>
            {logs.map(log => (
                <Container log={log} />
            ))}
        </div>
    )
}
